import { useTranslation } from "react-i18next";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Pencil } from "lucide-react";
import { Icon, cn } from "@nexus/ui";
import type { UserKpiEntry } from "@nexus/contracts";
import { kpiColorCssVar } from "@nexus/kpi-formula";

export type UserKpiTone = "green" | "red" | "neutral";
export type UserKpiBoxLayout = "default" | "compact";

const TONE_CLASS: Record<UserKpiTone, string> = {
  green: "text-green-600 dark:text-green-400",
  red: "text-red-600 dark:text-red-400",
  neutral: "text-muted-foreground",
};

export interface UserKpiBoxProps {
  entry: UserKpiEntry;
  value: string;
  badgeText: string | null;
  tone: UserKpiTone;
  layout?: UserKpiBoxLayout;
  isLoading?: boolean;
  draggable?: boolean;
  onEdit?: (entry: UserKpiEntry) => void;
  onToggleHidden?: (entry: UserKpiEntry) => void;
}

export function UserKpiBox({
  entry,
  value,
  badgeText,
  tone,
  layout = "default",
  isLoading = false,
  draggable = true,
  onEdit,
  onToggleHidden,
}: UserKpiBoxProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: entry.name,
    disabled: !draggable,
  });

  const gripButton = (
    <button
      type="button"
      className={cn(
        "flex size-5 shrink-0 items-center justify-center rounded text-muted-foreground/50",
        draggable
          ? "cursor-grab hover:text-muted-foreground active:cursor-grabbing"
          : "cursor-not-allowed",
      )}
      aria-label={t("kpiSettings.reorder", "Reorder {{name}}", { name: entry.displayName })}
      disabled={!draggable}
      {...attributes}
      {...listeners}
    >
      <GripVertical className="size-3.5" aria-hidden />
    </button>
  );

  const colorDot = (
    <span
      className="size-2.5 shrink-0 rounded-full"
      style={{ background: kpiColorCssVar(entry.colorToken, entry.index) }}
      aria-hidden
    />
  );

  const hiddenGlyph =
    entry.hidden ? (
      <Icon
        name="eye-off"
        size="xs"
        color="muted"
        aria-label={t("kpiSettings.hidden", "Hidden")}
      />
    ) : null;

  const toggleHiddenButton =
    onToggleHidden ? (
      <button
        type="button"
        className="flex size-5 shrink-0 items-center justify-center rounded text-muted-foreground/60 opacity-0 transition-opacity hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100"
        aria-label={
          entry.hidden
            ? t("kpiSettings.unhide", "Unhide {{name}}", { name: entry.displayName })
            : t("kpiSettings.hide", "Hide {{name}}", { name: entry.displayName })
        }
        onClick={() => onToggleHidden(entry)}
      >
        <Icon name={entry.hidden ? "eye" : "eye-off"} size="xs" color="inherit" />
      </button>
    ) : null;

  const editButton =
    onEdit ? (
      <button
        type="button"
        className="flex size-5 shrink-0 items-center justify-center rounded text-muted-foreground/60 opacity-0 transition-opacity hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100"
        aria-label={t("kpiSettings.editKpi", "Edit {{name}}", { name: entry.displayName })}
        onClick={() => onEdit(entry)}
      >
        <Pencil className="size-3" aria-hidden />
      </button>
    ) : null;

  const valueBlock = isLoading ? (
    <div className={cn("flex flex-col gap-1.5", layout === "default" && "pl-7")}>
      <div className="skeleton-shimmer-bar h-[19px] w-20 rounded" aria-hidden />
      <div className="skeleton-shimmer-bar h-4 w-14 rounded-full" aria-hidden />
    </div>
  ) : (
    <div className={cn("flex flex-col gap-0.5", layout === "default" && "pl-7")}>
      <p className="text-base font-medium tabular-nums text-foreground">{value}</p>
      {badgeText && (
        <p className={cn("text-xs font-medium tabular-nums", TONE_CLASS[tone])}>{badgeText}</p>
      )}
    </div>
  );

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "group relative flex min-w-0 flex-col gap-1 rounded-lg border border-border bg-muted/30 px-3 py-2.5",
        isDragging && "z-10 shadow-md",
        entry.hidden && "border-dashed bg-transparent",
      )}
      data-testid={`user-kpi-box-${entry.name}`}
    >
      {layout === "compact" ? (
        <>
          <div className="flex items-center gap-1.5">
            {gripButton}
            {colorDot}
            <div className="min-w-0 flex-1" aria-hidden />
            {hiddenGlyph}
            {toggleHiddenButton}
            {editButton}
          </div>
          <p
            className="line-clamp-2 min-h-[24px] text-[10px] font-normal uppercase leading-[13px] tracking-[0.04em] text-muted-foreground"
            title={entry.displayName}
          >
            {entry.displayName}
          </p>
          {valueBlock}
        </>
      ) : (
        <>
          <div className="flex items-center gap-1.5">
            {gripButton}
            {colorDot}
            <p
              className="min-w-0 flex-1 truncate text-[11px] font-medium uppercase tracking-wide text-muted-foreground"
              title={entry.displayName}
            >
              {entry.displayName}
            </p>
            {hiddenGlyph}
            {toggleHiddenButton}
            {editButton}
          </div>
          {valueBlock}
        </>
      )}
    </div>
  );
}
